import { ApiClient, HttpMethod } from "./ApiClient";
import { AppFwConfiguration } from "../AppFwContext.ts";

export interface NotificationsConfigResponse {
    readonly enabled: boolean;
}

export interface User {
    readonly id: string
    readonly name?: string
    readonly oidcName?: string
    readonly email?: string
    readonly avatarUrl?: string
}

export interface Credentials {
    readonly sessionId: string;
    readonly user: User;
}

interface AppFwErrorResponseRecord {
    readonly code: string;
    readonly message?: string;
    readonly parameters?: any;
}

function isAppFwErrorResponseRecord(obj: any): obj is AppFwErrorResponseRecord {
    return (
        typeof obj === "object" &&
        obj !== null &&
        typeof obj.code === "string"
    );
}

interface AppFwErrorResponse {
    readonly errors: AppFwErrorResponseRecord[];
}

function isAppFwErrorResponse(obj: any): obj is AppFwErrorResponse {
    return (
        typeof obj === "object" &&
        obj !== null &&
        Array.isArray(obj.errors) &&
        obj.errors.every(isAppFwErrorResponseRecord)
    );
}

export interface AppFwError {
    readonly errorRecords?: AppFwErrorResponseRecord[];
    readonly message: string;
}

export async function handleError(e: unknown): Promise<AppFwError> {

    let message = "Unknown error";
    let response: AppFwErrorResponseRecord[] | undefined = undefined;

    if (e instanceof Response) {
        try {
            const data = await e.clone().json()
            if (isAppFwErrorResponse(data)) {
                response = data.errors;
                message = data.errors.map(err => err.message || err.code)
                    .filter(Boolean)
                    .join("; ") || message;
            } else {
                message = JSON.stringify(data);
            }
        } catch {
            message = await e.text();
        }
    } else if (isAppFwErrorResponse(e)) {
        response = e.errors;
        message = e.errors.map(err => err.message).filter(Boolean).join("; ") || message;
    } else if (e instanceof Error) {
        message = e.message;
    }

    const error: AppFwError = { errorRecords: response, message }
    console.warn(error)
    return error
}


export class AppFwApiClient extends ApiClient {

    constructor(readonly appFwConfiguration: AppFwConfiguration) {
        super(process.env.NODE_ENV === 'production' ? 'https://api.eventer.app/' : 'http://localhost:8888/')
    }

    startOidcLoginUrl(provider: string): string {
        return `${this.baseUrl}v1/appfw/auth/oidc/${this.appFwConfiguration.appName}/${provider}/start`
    }

    async sessionAuth(
        params: { sessionId: string; },
    ): Promise<Credentials> {
        return this.fetch<Credentials>(`v1/appfw/auth/session/${this.appFwConfiguration.appName}`, {
            params: { sessionId: params.sessionId }
        })
    }

    async logout(params: { sessionId: string; }) {
        return this.fetch<undefined>('v1/appfw/auth/logout', { sessionId: params.sessionId })
    }

    async getNotificationsConfig(): Promise<NotificationsConfigResponse> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<NotificationsConfigResponse>(
            `v1/appfw/notifications/config`,
            {
                sessionId: sessionId,
                method: HttpMethod.GET,
            },
        )
    }

    async updateNotificationsConfig(params: { enabled: boolean }) {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<undefined>(
            `v1/appfw/notifications/config`,
            {
                sessionId: sessionId,
                method: HttpMethod.PATCH,
                params: params,
            },
        )
    }

    async updateWebPushSubscription(params: {
        installationId: string;
        subscription: PushSubscription | null;
    }) {
        let payload: undefined | PushSubscriptionJSON
        if (params.subscription) {
            payload = params.subscription.toJSON()
        }
        return this.put<undefined>(
            `v1/appfw/notifications/webpush/${params.installationId}`,
            payload
        )
    }

    async updateUserName(params: { userId: string, name: string }) {
        return this.put<undefined>(
            `v1/appfw/${this.appFwConfiguration.appName}/users/${params.userId}/name`,
            { value: params.name }
        )
    }

    async resetUserName(params: { userId: string }) {
        return this.delete<undefined>(
            `v1/appfw/${this.appFwConfiguration.appName}/users/${params.userId}/name`
        )
    }

    async getUser(params: { id: string; }): Promise<User> {
        return this.get<User>(`v1/appfw/${this.appFwConfiguration.appName}/users/${params.id}`)
    }
}